import { NavLink } from "react-router-dom";
import { ChevronsLeft } from "lucide-react";
import { cn } from "@/lib/cn";
import { NAV_ITEMS } from "@/app/nav";
import { OutsourceAccessLogo } from "@/components/brand/OutsourceAccessLogo";

interface SidebarContentProps {
  collapsed?: boolean;
  onNavigate?: () => void;
  badgeCounts?: Partial<Record<string, number>>;
}

export function SidebarContent({ collapsed, onNavigate, badgeCounts }: SidebarContentProps) {
  return (
    <div className="flex h-full flex-col">
      <div className={cn("flex flex-col gap-2 px-4 pt-5 pb-4", collapsed && "items-center px-2")}>
        <OutsourceAccessLogo markOnly={collapsed} />
        {!collapsed && (
          <p className="truncate pl-[42px] text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted">
            OA-Mary
          </p>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2.5 py-2">
        <ul className="flex flex-col gap-0.5">
          {NAV_ITEMS.map((item) => {
            const count = badgeCounts?.[item.label];
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/80 transition-colors",
                      "hover:bg-sidebar-hover hover:text-sidebar-foreground",
                      isActive && "bg-sidebar-active text-white shadow-sm hover:bg-sidebar-active hover:text-white",
                      collapsed && "justify-center px-0",
                    )
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="size-4 shrink-0" />
                  {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                  {!collapsed && Boolean(count) && (
                    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-sidebar-chip px-1 text-[11px] font-semibold text-sidebar-chip-foreground">
                      {count}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

export function DesktopSidebar({
  collapsed,
  onToggleCollapsed,
  badgeCounts,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  badgeCounts?: Partial<Record<string, number>>;
}) {
  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 lg:flex",
        collapsed ? "w-[76px]" : "w-64",
      )}
    >
      <SidebarContent collapsed={collapsed} badgeCounts={badgeCounts} />
      <button
        onClick={onToggleCollapsed}
        className="mx-2.5 mb-3 flex items-center justify-center gap-2 rounded-lg border border-sidebar-border py-2 text-xs font-medium text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground"
      >
        <ChevronsLeft className={cn("size-3.5 transition-transform", collapsed && "rotate-180")} />
        {!collapsed && "Collapse"}
      </button>
    </aside>
  );
}
